import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  queryOptions,
  infiniteQueryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type Query,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEqual } from 'lodash-es';
import { api } from 'lib/request/api';
import { subscribe } from 'lib/pusher';
import { useLatest } from 'lib/hooks/use-latest';
import { useDocumentChannelEvents } from 'lib/hooks/use-document-channel-events';
import { armJobCompletion, type JobChannel } from 'lib/hooks/useJobCompletion';
import { actionErrorMessage, apiErrorMessage } from 'utils/api-errors';
import { endSentence } from 'utils/strings';
import type { PartyStatus } from 'components/status-tag';
import {
  addNotificationsAtom,
  NotificationGroup,
} from 'components/atomised-notifications';
import { isFlagOverrideRequired } from 'components/flag-override-modal';
import type {
  DocumentResponse,
  ReconcileCreateResponse,
} from 'types/api-responses';
import type {
  ApiPayloads,
  ResolutionChangeReason,
  StructuredPartyActionError,
} from 'types/api-payloads';
import {
  selectedCostWellIdAtom,
  selectedWellIndexAtom as selectedAfeWellIndexAtom,
} from 'pages/afe-catalog/structuring/atoms/layout';
import {
  fetchWellOverviewAtom as fetchAfeWellOverviewAtom,
  wellOverviewDataAtom as afeWellOverviewDataAtom,
  wellOverviewLoadingAtom as afeWellOverviewLoadingAtom,
  wellOverviewUrl as afeWellOverviewUrl,
} from 'pages/afe-catalog/structuring/atoms/wells';
import {
  costOverviewDataAtom,
  costOverviewUrl,
  fetchCostOverviewAtom,
} from 'pages/afe-catalog/structuring/atoms/costs';
import {
  createIdentificationResourceAtom,
  identificationUrl,
} from 'pages/afe-catalog/structuring/atoms/identification';
import type { WellSectionStatus } from 'pages/afe-catalog/structuring/types';
import {
  fetchInterestOverviewAtom,
  interestOverviewDataAtom,
  interestOverviewLoadingAtom,
  interestOverviewUrl,
} from 'pages/division-orders/structuring/atoms/interests';
import { deriveInterestSectionStatus } from 'pages/division-orders/structuring/interest-utils';
import {
  createOverviewResourceAtom as createDoOverviewResourceAtom,
  overviewUrl as doOverviewUrl,
} from 'pages/division-orders/structuring/atoms/overview';
import type { DoSectionStatus } from 'pages/division-orders/structuring/types-overview';
import { selectedWellIndexAtom as selectedDoWellIndexAtom } from 'pages/division-orders/structuring/atoms/layout';
import {
  fetchWellOverviewAtom as fetchDoWellOverviewAtom,
  wellOverviewDataAtom as doWellOverviewDataAtom,
  wellOverviewLoadingAtom as doWellOverviewLoadingAtom,
  wellOverviewUrl as doWellOverviewUrl,
} from 'pages/division-orders/structuring/atoms/wells';
import type { WellListItem as DoWellListItem } from 'pages/division-orders/structuring/types-well';
import {
  createOverviewResourceAtom as createRcOverviewResourceAtom,
  overviewUrl as rcOverviewUrl,
} from 'pages/royalty-checks/structuring/atoms/overview';
import type { RcSectionStatus } from 'pages/royalty-checks/structuring/types';
import {
  fetchLineItemOverviewAtom,
  lineItemOverviewDataAtom,
  lineItemOverviewUrl,
} from 'pages/royalty-checks/structuring/atoms/line-items';
import { deriveLineItemSectionStatus } from 'pages/royalty-checks/structuring/line-item-utils';
import {
  createFinancialSummaryResourceAtom,
  financialSummaryUrl,
} from 'pages/royalty-checks/structuring/atoms/financial-summary';
import {
  selectedLineItemWellIndexAtom,
  selectedWellIndexAtom as selectedRcWellIndexAtom,
} from 'pages/royalty-checks/structuring/atoms/layout';
import {
  fetchWellOverviewAtom as fetchRcWellOverviewAtom,
  wellOverviewDataAtom as rcWellOverviewDataAtom,
  wellOverviewLoadingAtom as rcWellOverviewLoadingAtom,
  wellOverviewUrl as rcWellOverviewUrl,
} from 'pages/royalty-checks/structuring/atoms/wells';
import type { WellListItem as RcWellListItem } from 'pages/royalty-checks/structuring/types-well';
import {
  CLIENT_SESSION_ID,
  canonizePartyAtom,
  confirmSuggestionAtom,
  fetchStructuredPartiesListAtom,
  partyDetailsData,
  partyUrl,
  removePartyAtom,
  rerunDocumentAtom,
  rerunPartyAtom,
  restorePartyAtom,
  revertSuggestionAtom,
  saveUserSelectionAtom,
  structuredPartiesListDataAtom,
  updatePartyDetailsAtom,
} from './atoms/structured-parties';
import {
  reconcileCreateAtom,
  reconcileSessionId,
  reconcileUndoAtom,
  reconcileUndoStackAtom,
  reconcileUndoUrl,
} from './atoms/reconcile';
import {
  moveAddressToDocumentAtom,
  moveAddressToDocumentUrl,
  moveAddressToPartyAtom,
  moveAddressToPartyUrl,
} from './atoms/structured-addresses';
import { usePartyModals } from './party-modal-hooks';
import { reconcileOutcomeLabel } from './reconcile-result-banner';
import type { StagedEntityEditorHandle } from './staged-entity-editor';
import { OVERVIEW_ANCHORS, sharedAnchorMessage } from './utils';
import type {
  AuditFeedResponse,
  MoveAddressDestination,
  OverviewAnchor,
  SharedRenameModalProps,
  SharedWith,
  StructuredPartiesListResponse,
  StructuredPartyDetail,
  StructuredPartyListItem,
  SuggestionSummary,
} from './types';

const QUERY_KEY = ['documents', 'structuring'] as const;

/** How often a running Well Matcher or match re-run is checked on. */
const RUN_POLL_MS = 3000;

/** How many reads in a row may fail before a poll gives up (the first try plus two retries). */
const POLL_ATTEMPTS = 3;

/**
 * The path the `api` client takes for a server href: it adds its own prefix, so the leading slash
 * goes.
 *
 * @param {string} href - An href as the server sends it.
 * @return {string} The href without its leading slash.
 */
const apiPath = (href: string): string => href.replace(/^\//, '');

/**
 * Query options for a party's detail. The detail a view shows stays as loaded until refetched or
 * replaced, and is dropped once no view shows it, so a party opened again loads afresh. Each load
 * is a new object, even when nothing changed: the views tell a reload apart from the detail it
 * replaced. A one-off `read` (a live-update check, a run poll) is its own query under the same
 * key, so it never replaces the detail on screen and one in flight from before a party switch is
 * never shared.
 *
 * @param {string} href - The party's href.
 * @param {number} read - A one-off read's number; none for the detail a view shows.
 * @return {object} The detail's query options.
 */
export function partyDetailOptions(href: string, read?: number) {
  return queryOptions({
    queryKey:
      read === undefined
        ? ['party-detail', href]
        : ['party-detail', href, 'read', read],
    queryFn: ({ signal }) =>
      api.get(apiPath(href), { signal }).json<StructuredPartyDetail>(),
    staleTime: read === undefined ? Infinity : 0,
    gcTime: 0,
    retry: false,
    structuralSharing: false,
  });
}

let detailReads = 0;

/**
 * Reads a party's detail afresh, for a view that does not show it.
 *
 * @return {Function} `(href)` returning a promise for the detail; rejects when the read fails.
 */
export function useReadPartyDetail() {
  const queryClient = useQueryClient();
  return useCallback(
    (href: string) => {
      detailReads += 1;
      return queryClient.fetchQuery(partyDetailOptions(href, detailReads));
    },
    [queryClient]
  );
}

/**
 * A party's detail as its view shows it. A refetch keeps the loaded party on screen, failed or
 * not; the latest refetch or replacement wins over any refetch still in flight.
 *
 * @param {string} href - The party's href.
 * @return {object} The detail (null until loaded), whether a load is in flight, whether the last
 *   refetch failed, `refetch`, and `show` to replace it with a detail read elsewhere.
 */
export function usePartyDetail(href: string) {
  const queryClient = useQueryClient();
  const query = useQuery(partyDetailOptions(href));
  const { refetch } = query;
  return {
    detail: query.data ?? null,
    loading: query.isFetching,
    refreshFailed: query.isRefetchError,
    refetch: useCallback(() => {
      void refetch();
    }, [refetch]),
    show: useCallback(
      (fresh: StructuredPartyDetail) => {
        const { queryKey } = partyDetailOptions(href);
        void queryClient.cancelQueries({ queryKey, exact: true });
        queryClient.setQueryData(queryKey, fresh);
      },
      [queryClient, href]
    ),
  };
}

/**
 * Query options for the poll on a well overview while a Well Matcher run is in flight. The read
 * goes through the doctype's fetch atom, which stores the overview, so the query holds nothing of
 * its own: it only keeps time. It starts out fresh, so its first read is one interval out (the
 * overview was just loaded), and it pauses while the tab is hidden.
 *
 * @param {string} url - The well overview's URL.
 * @param {Function} reload - The doctype's fetch-atom setter for that overview.
 * @return {object} The poll's query options.
 */
export function wellOverviewPollOptions(
  url: string,
  reload: (url: string) => Promise<void>
) {
  return queryOptions({
    queryKey: [...QUERY_KEY, 'well-overview-poll', url],
    queryFn: async () => {
      await reload(url);
      return null;
    },
    initialData: null,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Polls a well overview every few seconds while `running`; the flag flips false on the read after
 * the run finishes, which ends the poll.
 *
 * @param {string} url - The well overview's URL.
 * @param {Function} reload - The doctype's fetch-atom setter for that overview.
 * @param {boolean} running - Whether a run is in flight.
 */
export function useWellOverviewPoll(
  url: string,
  reload: (url: string) => Promise<void>,
  running: boolean
) {
  useQuery({
    ...wellOverviewPollOptions(url, reload),
    enabled: running,
    refetchInterval: running ? RUN_POLL_MS : false,
  });
}

/**
 * The key of one run-progress poll.
 *
 * @param {string} owner - A key unique to the hook instance that polls.
 * @param {string} scope - What the poll reads, such as a party's href.
 * @param {number} generation - Bumped to restart a poll that gave up.
 * @return {Array} The poll's query key.
 */
const runProgressPollKey = (owner: string, scope: string, generation: number) =>
  [...QUERY_KEY, 'run-progress-poll', owner, scope, generation] as const;

/**
 * Query options for a poll that stands in for a run's progress: `read` throws when the progress
 * could not be read, and after {@link POLL_ATTEMPTS} failures in a row the query errors and the
 * poll stops. Each poll is keyed by its owner, what it polls and a generation, so the owner
 * restarts a poll that gave up by moving to the next generation, whose first read is immediate;
 * generation 0's is one interval out unless `immediate`.
 *
 * @param {object} poll - The poll.
 * @param {string} poll.owner - A key unique to the hook instance that polls.
 * @param {string} poll.scope - What the poll reads, such as a party's href.
 * @param {number} poll.generation - Bumped to restart a poll that gave up.
 * @param {boolean} poll.immediate - Whether every start reads at once, not one interval out.
 * @param {Function} poll.read - One read of the progress.
 * @return {object} The poll's query options.
 */
export function runProgressPollOptions<T>({
  owner,
  scope,
  generation,
  immediate,
  read,
}: {
  owner: string;
  scope: string;
  generation: number;
  immediate: boolean;
  read: () => Promise<T>;
}) {
  return queryOptions<T | null>({
    queryKey: runProgressPollKey(owner, scope, generation),
    queryFn: read,
    initialData: null,
    staleTime: (query: Query<T | null>) =>
      immediate || (generation > 0 && query.state.dataUpdateCount === 0)
        ? 0
        : Infinity,
    retry: POLL_ATTEMPTS - 1,
    retryDelay: RUN_POLL_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query: Query<T | null>) =>
      query.state.status === 'error' ? false : RUN_POLL_MS,
  });
}

/**
 * Polls a run's progress while `running`, and says once when the poll gives up. A poll that stops
 * running, or moves to another scope, is reset: its failed reads in flight are dropped, so they
 * never surface as a give-up later. The owner clears its record of the give-up with `restart`,
 * which also makes the next start read at once.
 *
 * @param {object} options - The poll.
 * @param {boolean} options.running - Whether the run is in flight.
 * @param {string} options.scope - What the poll reads; a new scope polls afresh.
 * @param {boolean} options.immediate - Whether every start reads at once.
 * @param {Function} options.read - One read of the progress; throws when it can't be read.
 * @param {Function} options.onGiveUp - Called in render, once per give-up.
 * @return {object} `restart`, to poll afresh after a give-up.
 */
export function useRunProgressPoll<T>({
  running,
  scope,
  immediate,
  read,
  onGiveUp,
}: {
  running: boolean;
  scope: string;
  immediate: boolean;
  read: () => Promise<T>;
  onGiveUp: () => void;
}) {
  const queryClient = useQueryClient();
  const owner = useId();
  const [generation, setGeneration] = useState(0);
  const poll = useQuery({
    ...runProgressPollOptions({ owner, scope, generation, immediate, read }),
    enabled: running,
  });
  useEffect(() => {
    if (!running) return;
    const queryKey = runProgressPollKey(owner, scope, generation);
    return () => {
      // Cancelling stops the retries in flight; removing drops the failure so it can't surface.
      void queryClient
        .cancelQueries({ queryKey, exact: true })
        .then(() => queryClient.removeQueries({ queryKey, exact: true }));
    };
  }, [running, queryClient, owner, scope, generation]);
  // Held per give-up so the owner hears of each one once.
  const [heardGiveUpAt, setHeardGiveUpAt] = useState(0);
  if (running && poll.isError && poll.errorUpdatedAt !== heardGiveUpAt) {
    setHeardGiveUpAt(poll.errorUpdatedAt);
    onGiveUp();
  }
  const restart = useCallback(() => setGeneration((g) => g + 1), []);
  return { restart };
}

/**
 * A run as a view sees it: one the view started and has not yet seen finish, or one its snapshot
 * (the parties list, a party's detail) reports; the poll on it; and the give-up, after which the
 * snapshot's run flag is not trusted until a newer snapshot loads or a new run starts.
 *
 * @param {object} options - The run.
 * @param {unknown} options.snapshot - What the run flag is read from; null until loaded.
 * @param {boolean} options.reportedRunning - The snapshot's run flag.
 * @param {string} options.scope - What the poll reads; a new scope polls afresh.
 * @param {boolean} options.pollable - Whether there is anything to poll.
 * @param {boolean} options.immediate - Whether every start of the poll reads at once.
 * @param {string} options.lostMessage - The error shown once the poll gives up.
 * @param {Function} options.setError - Shows, or clears, the view's error.
 * @param {Function} options.read - One read of the progress, given `finished` to call once the
 *   run has ended; throws when the progress can't be read.
 * @return {object} Whether the run is in flight, whether its progress was lost, and `start`,
 *   `finish` and `reset`.
 */
export function useRunProgress<S>({
  snapshot,
  reportedRunning,
  scope,
  pollable = true,
  immediate,
  lostMessage,
  setError,
  read,
}: {
  snapshot: S | null;
  reportedRunning: boolean;
  scope: string;
  pollable?: boolean;
  immediate: boolean;
  lostMessage: string;
  setError: (update: (current: string | null) => string | null) => void;
  read: (finished: () => void) => Promise<unknown>;
}) {
  // A run this view started and has not yet seen finish.
  const [requested, setRequested] = useState(false);
  // The snapshot whose run flag the poll gave up checking.
  const [lostOn, setLostOn] = useState<{ snapshot: S | null } | null>(null);
  // Lost only while the snapshot it was lost on is shown.
  const progressLost =
    !!snapshot && lostOn !== null && lostOn.snapshot === snapshot;
  const running = !progressLost && (requested || reportedRunning);

  const poll = useRunProgressPoll({
    running: running && pollable,
    scope,
    immediate,
    read: () => read(() => setRequested(false)),
    onGiveUp: () => {
      setRequested(false);
      setLostOn({ snapshot });
      setError(() => lostMessage);
    },
  });

  /** Trusts the run flag again, and polls afresh. */
  const clearLost = () => {
    setLostOn(null);
    poll.restart();
  };
  // A snapshot loaded or cleared since the poll gave up (by any surface)
  // carries a trustworthy run flag again.
  if (lostOn && snapshot !== lostOn.snapshot) {
    clearLost();
    setError((current) => (current === lostMessage ? null : current));
  }

  return {
    running,
    progressLost,
    start: () => {
      if (lostOn) clearLost();
      setRequested(true);
    },
    finish: () => setRequested(false),
    reset: () => {
      setRequested(false);
      if (lostOn) clearLost();
    },
  };
}

/**
 * Submits a Move Address modal. The modal awaits its submit and shows a thrown error inline, so the
 * move resolves on success and rejects with the server's message.
 *
 * @param {number} documentId - The document the address belongs to.
 * @param {Function} onMoved - Runs after a successful move.
 * @return {Function} `(addressId, destination)` returning a promise for the move.
 */
export function useMoveAddressSubmit(documentId: number, onMoved: () => void) {
  const triggerMoveToDocument = useSetAtom(moveAddressToDocumentAtom);
  const triggerMoveToParty = useSetAtom(moveAddressToPartyAtom);

  return useCallback(
    (addressId: number, destination: MoveAddressDestination) =>
      new Promise<void>((resolvePromise, rejectPromise) => {
        /** Settles the submit, then runs the caller's follow-up. */
        const onSuccess = () => {
          resolvePromise();
          onMoved();
        };
        /**
         * Rejects the submit with the server's message.
         *
         * @param {StructuredPartyActionError} err - The server's error body.
         */
        const onError = (err?: StructuredPartyActionError) =>
          rejectPromise(
            new Error(actionErrorMessage(err, 'Could not move address.'))
          );

        if (destination.type === 'document') {
          triggerMoveToDocument({
            url: moveAddressToDocumentUrl(documentId, addressId),
            type: 'POST',
            data: null,
            onSuccess,
            onError,
          });
        } else {
          triggerMoveToParty({
            url: moveAddressToPartyUrl(documentId, addressId),
            type: 'POST',
            data: { target_party_ids: destination.targetPartyIds },
            onSuccess,
            onError,
          });
        }
      }),
    [documentId, onMoved, triggerMoveToDocument, triggerMoveToParty]
  );
}

/**
 * Holds a rename of a shared anchor for confirmation, since the other parties sharing it show the
 * name too. A refresh of the party while the confirm is open closes it: the held save and the
 * disclosed count were for the old one.
 *
 * @param {object} party - The party whose staged or matched entity is being renamed.
 * @param {SharedWith | null | undefined} party.sharedWith - Who else shares its anchor.
 * @return {object} `guard(renamed, save)` to run a save through the confirm, and the modal's props.
 */
export function useSharedRenameConfirm(party: {
  sharedWith: SharedWith | null | undefined;
}) {
  const [pending, setPending] = useState<{
    save: () => void;
    party: typeof party;
  } | null>(null);
  if (pending && pending.party !== party) setPending(null);
  const current = pending?.party === party ? pending : null;
  const message = sharedAnchorMessage(party.sharedWith, 'Renaming this entity');
  /**
   * Runs a save, first through the confirm when it renames a shared anchor.
   *
   * @param {boolean} renamed - Whether the save changes the entity's name.
   * @param {Function} save - The save.
   */
  const guard = (renamed: boolean, save: () => void) => {
    if (renamed && message) setPending({ save, party });
    else save();
  };
  const modalProps: SharedRenameModalProps = {
    open: !!current,
    message,
    onConfirm: () => {
      setPending(null);
      current?.save();
    },
    onCancel: () => setPending(null),
  };
  return { guard, modalProps };
}

/**
 * The Move Address modal a party's rows open: one party's move at a time. A move reloads the
 * parties list, then runs `onMoved`.
 *
 * @param {object} options - Where the move happens.
 * @param {DocumentResponse} options.document - The document the addresses belong to.
 * @param {StructuredPartyListItem[]} options.allParties - The destinations the modal offers.
 * @param {StructuredPartyListItem} options.party - The one party a panel moves from, shown as it
 *   is now; without one, `open` names the party.
 * @param {Function} options.onMoved - Runs after a successful move.
 * @return {object} `open` and the modal's props.
 */
export function useMoveAddress({
  document,
  allParties,
  party: panelParty,
  onMoved,
}: {
  document: DocumentResponse;
  allParties: StructuredPartyListItem[];
  party?: StructuredPartyListItem;
  onMoved: () => void;
}) {
  const [openFor, setOpenFor] = useState<StructuredPartyListItem | null>(null);
  const party = openFor && (panelParty ?? openFor);
  /**
   * Opens the modal.
   *
   * @param {StructuredPartyListItem} target - The party to move from; a panel's own by default.
   */
  const open = (target?: StructuredPartyListItem) =>
    setOpenFor(target ?? panelParty ?? null);
  const reloadList = useSetAtom(fetchStructuredPartiesListAtom);
  const listHref = document.resources.structuredParties?.href;
  const onSubmit = useMoveAddressSubmit(
    document.id,
    useCallback(() => {
      setOpenFor(null);
      if (listHref) void reloadList(listHref);
      onMoved();
    }, [listHref, onMoved, reloadList])
  );
  return {
    open,
    modalProps: {
      open: party !== null,
      party,
      allParties,
      onClose: () => setOpenFor(null),
      onSubmit,
    },
  };
}

/**
 * Saves a StagedEntityEditor's changes to the party's details: none closes without a request, and
 * a rename of a shared anchor is confirmed first (see useSharedRenameConfirm).
 *
 * @param {object} options - The party and how the panel reports.
 * @param {number} options.documentId - The party's document.
 * @param {StructuredPartyDetail} options.party - The party being edited.
 * @param {string} options.failure - The message when the save fails without one.
 * @param {Function} options.onStart - Clears the panel's last error as a save starts.
 * @param {Function} options.onError - Shows a failed save's message.
 * @return {object} Whether a save is in flight, `save`, and the rename confirm's props.
 */
export function useStagedEditsSave({
  documentId,
  party,
  failure,
  onStart,
  onError,
}: {
  documentId: number;
  party: StructuredPartyDetail;
  failure: string;
  onStart: () => void;
  onError: (message: string) => void;
}) {
  const [updateState, triggerUpdate] = useAtom(updatePartyDetailsAtom);
  const renameConfirm = useSharedRenameConfirm(party);
  /**
   * Saves the editor's changes.
   *
   * @param {StagedEntityEditorHandle | null} editor - The editor.
   * @param {object} then - What follows.
   * @param {Function} then.onUnchanged - Runs, without a request, when nothing changed.
   * @param {Function} then.onSaved - Runs after the save.
   */
  const save = (
    editor: Pick<
      StagedEntityEditorHandle,
      'buildPatchPayload' | 'seededAnchorId'
    > | null,
    { onUnchanged, onSaved }: { onUnchanged: () => void; onSaved: () => void }
  ) => {
    if (!editor) return;
    const payload = editor.buildPatchPayload();
    if (!payload.hasChanges) {
      onUnchanged();
      return;
    }
    renameConfirm.guard(payload.name !== undefined, () => {
      onStart();
      void triggerUpdate({
        url: partyUrl(documentId, party.id, party.kind),
        type: 'PATCH',
        data: partyDetailsData(payload, editor.seededAnchorId),
        onSuccess: onSaved,
        onError: (err) => onError(actionErrorMessage(err, failure)),
      });
    });
  };
  return {
    saving: updateState.loading,
    save,
    renameModalProps: renameConfirm.modalProps,
  };
}

interface AfeStructuringOptions {
  documentId: number;
  // Document::Afe only. The hooks run on every document (the shell is reused
  // across a document switch), so this gates the fetches, not the hooks.
  enabled: boolean;
  rerunRunning: boolean;
  // A deep link opened a cost well: the shell switches to the overview.
  onDeepLink: () => void;
}

/**
 * AFE structuring state for the structuring shell: the well and cost overviews (module-level
 * atoms), the identification status, and the well / cost-well drill-in selection.
 *
 * @param {AfeStructuringOptions} options - The document and whether it is an AFE.
 * @return {object} The overviews, their statuses, the drill-in selection and reloads.
 */
export function useAfeStructuring({
  documentId,
  enabled,
  rerunRunning,
  onDeepLink,
}: AfeStructuringOptions) {
  // Deep-link target carried from the AFE Catalog detail page's Edit action:
  // `{ afeCostWellId }` opens the Cost section drilled into that well's
  // Structure Costs on mount.
  const location = useLocation<{ afeCostWellId?: number } | undefined>();
  // Guards the deep-link drill-in so it runs at most once per mount: there is
  // no router clear-API here to blank location.state, so without this the
  // effect could re-fire the staggered scroll if its deps change while the same
  // state object is still attached to the history entry.
  const deepLinkHandledRef = useRef(false);
  const onDeepLinkRef = useLatest(onDeepLink);

  const [selectedWellIndex, setSelectedWellIndex] = useAtom(
    selectedAfeWellIndexAtom
  );
  const [selectedCostWellId, setSelectedCostWellId] = useAtom(
    selectedCostWellIdAtom
  );
  const wellOverview = useAtomValue(afeWellOverviewDataAtom);
  const wellOverviewLoading = useAtomValue(afeWellOverviewLoadingAtom);
  const fetchWells = useSetAtom(fetchAfeWellOverviewAtom);
  const fetchCosts = useSetAtom(fetchCostOverviewAtom);
  const wells = wellOverview?.wells ?? [];
  const selectedWell =
    selectedWellIndex !== null ? (wells[selectedWellIndex] ?? null) : null;

  const costOverview = useAtomValue(costOverviewDataAtom);
  // A cost well's `status` is its cost-review progress (never 'removed');
  // removal is the separate `removed` flag. Removed wells still render in the
  // tree, struck through, but are left out of the status counts.
  const costWells = costOverview?.wells ?? [];
  const activeCostWells = costWells.filter((w) => !w.removed);
  const costCompleteCount = activeCostWells.filter(
    (w) => w.status === 'complete'
  ).length;
  // Mirrors CostOverviewSection's getCostSectionStatus.
  const costSectionStatus: PartyStatus =
    costCompleteCount === 0
      ? 'not_started'
      : costCompleteCount === activeCostWells.length
        ? 'complete'
        : 'in_progress';

  // Reads the same resource IdentificationSection uses (one extra lightweight
  // GET on mount for AFE documents).
  const identificationAtom = useMemo(
    () =>
      createIdentificationResourceAtom(
        enabled ? identificationUrl(documentId) : undefined
      ),
    [enabled, documentId]
  );
  const identificationResource = useAtomValue(identificationAtom);
  const identificationData = identificationResource.data;
  const identificationStatus: WellSectionStatus =
    identificationData && !('error' in identificationData)
      ? (identificationData.status ?? 'not_started')
      : 'not_started';

  // Hydrate the well + cost overviews on mount so the tree's well leaves and
  // the overview sections render on first paint. The overview atoms are
  // module-level, so also clear any drill-in carried over from a previously
  // viewed document.
  useEffect(() => {
    if (!enabled) return undefined;
    setSelectedWellIndex(null);
    const deepLinkCostWellId = location.state?.afeCostWellId ?? null;
    const timeoutIds: number[] = [];
    if (typeof deepLinkCostWellId === 'number' && !deepLinkHandledRef.current) {
      deepLinkHandledRef.current = true;
      onDeepLinkRef.current();
      setSelectedCostWellId(deepLinkCostWellId);
      // Scrolled here, not in a selectedCostWellId effect: that atom is
      // module-level and may already hold this id from a prior visit, so the
      // no-op set wouldn't re-fire a dependent effect. The page settles in
      // several steps after the deep link mounts (the overview sections unmount
      // as the drill-in takes over, the sticky header collapses on first
      // scroll, the cost data loads async), so re-assert the alignment across a
      // short window; the element's `scroll-margin-top` clears the header.
      /** Scrolls the drilled-in cost well's Structure Costs into view. */
      const scrollToCostDetail = () => {
        window.document
          .getElementById('afe-cost-detail')
          ?.scrollIntoView({ block: 'start' });
      };
      [0, 120, 280, 500, 800, 1150].forEach((ms) =>
        timeoutIds.push(window.setTimeout(scrollToCostDetail, ms))
      );
    } else if (deepLinkCostWellId === null) {
      setSelectedCostWellId(null);
    }
    void fetchWells(afeWellOverviewUrl(documentId));
    void fetchCosts(costOverviewUrl(documentId));
    return () => timeoutIds.forEach((id) => window.clearTimeout(id));
  }, [
    enabled,
    documentId,
    location.state,
    fetchWells,
    fetchCosts,
    setSelectedWellIndex,
    setSelectedCostWellId,
    onDeepLinkRef,
  ]);

  // Poll the well overview while a Well Matcher run (or the document re-run) is
  // in flight so the per-well and section spinners flip off as each workflow
  // completes.
  useWellOverviewPoll(
    afeWellOverviewUrl(documentId),
    fetchWells,
    enabled && (rerunRunning || !!wellOverview?.agentRunInProgress)
  );

  return {
    wellOverview,
    wellOverviewLoading,
    wells,
    selectedWellIndex,
    setSelectedWellIndex,
    selectedWell,
    costOverview,
    costWells,
    costSectionStatus,
    selectedCostWellId,
    setSelectedCostWellId,
    identificationStatus,
    reloadWells: () => fetchWells(afeWellOverviewUrl(documentId)),
    reloadCosts: () => fetchCosts(costOverviewUrl(documentId)),
  };
}

interface DoctypeStructuringOptions {
  documentId: number;
  // This doctype only; gates the fetches, not the hooks.
  enabled: boolean;
}

/**
 * Division order structuring state for the structuring shell. The overview DATA atoms are
 * module-level (shared across DO documents); the well drill-in SELECTION is document-scoped (an
 * atomFamily keyed on the document), so it never bleeds across documents.
 *
 * @param {DoctypeStructuringOptions} options - The document and whether it is a division order.
 * @return {object} The overviews, their statuses, the drill-in selection and reloads.
 */
export function useDivisionOrderStructuring({
  documentId,
  enabled,
}: DoctypeStructuringOptions) {
  const interestOverview = useAtomValue(interestOverviewDataAtom);
  const interestOverviewLoading = useAtomValue(interestOverviewLoadingAtom);
  const fetchInterests = useSetAtom(fetchInterestOverviewAtom);
  const clearInterests = useSetAtom(interestOverviewDataAtom);
  const interests = useMemo(
    () => interestOverview?.interests ?? [],
    [interestOverview?.interests]
  );
  // The same derivation as the section header, so the tree icon and the
  // section tag never disagree.
  const { status: interestSectionStatus } =
    deriveInterestSectionStatus(interests);

  const selectedWellAtom = useMemo(
    () => selectedDoWellIndexAtom(documentId),
    [documentId]
  );
  const [selectedWellIndex, setSelectedWellIndex] = useAtom(selectedWellAtom);
  const wellOverview = useAtomValue(doWellOverviewDataAtom);
  const wellOverviewLoading = useAtomValue(doWellOverviewLoadingAtom);
  const fetchWells = useSetAtom(fetchDoWellOverviewAtom);
  const clearWells = useSetAtom(doWellOverviewDataAtom);
  const wells: DoWellListItem[] = useMemo(
    () => wellOverview?.wells ?? [],
    [wellOverview?.wells]
  );
  const selectedWell =
    selectedWellIndex !== null ? (wells[selectedWellIndex] ?? null) : null;

  // Reads the same resource OverviewSection uses (one lightweight GET on mount).
  const overviewAtom = useMemo(
    () =>
      createDoOverviewResourceAtom(
        enabled ? doOverviewUrl(documentId) : undefined
      ),
    [enabled, documentId]
  );
  const overviewData = useAtomValue(overviewAtom).data;
  const overviewStatus: DoSectionStatus =
    overviewData && !('error' in overviewData)
      ? (overviewData.status ?? 'not_started')
      : 'not_started';

  // Clear the module-level overview atoms FIRST so a document switch never
  // shows the previous DO's wells/interests before the new fetch resolves (and
  // a failed fetch can't leave stale data on screen); reset the drill-in so a
  // remount lands on the overview.
  useEffect(() => {
    if (!enabled) return;
    clearWells(null);
    clearInterests(null);
    void fetchWells(doWellOverviewUrl(documentId));
    setSelectedWellIndex(null);
    void fetchInterests(interestOverviewUrl(documentId));
  }, [
    enabled,
    documentId,
    clearInterests,
    clearWells,
    fetchInterests,
    fetchWells,
    setSelectedWellIndex,
  ]);

  // Poll the well overview while a Well Matcher run is in flight.
  useWellOverviewPoll(
    doWellOverviewUrl(documentId),
    fetchWells,
    enabled && !!wellOverview?.agentRunInProgress
  );

  return {
    overviewStatus,
    interestOverview,
    interestOverviewLoading,
    interestSectionStatus,
    wellOverview,
    wellOverviewLoading,
    wells,
    selectedWellIndex,
    setSelectedWellIndex,
    selectedWell,
    reloadWells: () => fetchWells(doWellOverviewUrl(documentId)),
    reloadInterests: () => fetchInterests(interestOverviewUrl(documentId)),
  };
}

/**
 * Royalty check structuring state for the structuring shell. The overview DATA atoms are
 * module-level (shared across checks); the well and line-item drill-in SELECTIONS are
 * document-scoped and independent of each other.
 *
 * @param {DoctypeStructuringOptions} options - The document and whether it is a royalty check.
 * @return {object} The sections, their statuses, the drill-in selections and reloads.
 */
export function useRoyaltyCheckStructuring({
  documentId,
  enabled,
}: DoctypeStructuringOptions) {
  const overviewAtom = useMemo(
    () =>
      createRcOverviewResourceAtom(
        enabled ? rcOverviewUrl(documentId) : undefined
      ),
    [enabled, documentId]
  );
  const overviewData = useAtomValue(overviewAtom).data;
  const overviewStatus: RcSectionStatus =
    overviewData && !('error' in overviewData)
      ? (overviewData.status ?? 'not_started')
      : 'not_started';

  const lineItemOverview = useAtomValue(lineItemOverviewDataAtom);
  const clearLineItems = useSetAtom(lineItemOverviewDataAtom);
  const fetchLineItems = useSetAtom(fetchLineItemOverviewAtom);
  const lineItemSectionStatus = deriveLineItemSectionStatus(
    lineItemOverview?.summary
  );
  const lineItemGroups = useMemo(
    () => lineItemOverview?.groups ?? [],
    [lineItemOverview?.groups]
  );
  const selectedLineItemAtom = useMemo(
    () => selectedLineItemWellIndexAtom(documentId),
    [documentId]
  );
  const [selectedLineItemIndex, setSelectedLineItemIndex] =
    useAtom(selectedLineItemAtom);
  const selectedLineItemGroup =
    selectedLineItemIndex !== null
      ? (lineItemGroups[selectedLineItemIndex] ?? null)
      : null;

  // The financial summary exists only for PDF checks; it drives both the tree
  // node and whether the section renders at all.
  const summaryAtom = useMemo(
    () =>
      createFinancialSummaryResourceAtom(
        enabled ? financialSummaryUrl(documentId) : undefined
      ),
    [enabled, documentId]
  );
  const summaryResponse = useAtomValue(summaryAtom).data;
  const summaryData =
    summaryResponse && !('error' in summaryResponse) ? summaryResponse : null;
  const summaryAvailable = !!summaryData?.available;
  const summaryStatus: RcSectionStatus = summaryData?.status ?? 'not_started';

  const selectedWellAtom = useMemo(
    () => selectedRcWellIndexAtom(documentId),
    [documentId]
  );
  const [selectedWellIndex, setSelectedWellIndex] = useAtom(selectedWellAtom);
  const wellOverview = useAtomValue(rcWellOverviewDataAtom);
  const wellOverviewLoading = useAtomValue(rcWellOverviewLoadingAtom);
  const fetchWells = useSetAtom(fetchRcWellOverviewAtom);
  const clearWells = useSetAtom(rcWellOverviewDataAtom);
  const wells: RcWellListItem[] = useMemo(
    () => wellOverview?.wells ?? [],
    [wellOverview?.wells]
  );
  const selectedWell =
    selectedWellIndex !== null ? (wells[selectedWellIndex] ?? null) : null;

  // Clear the module-level atoms first so a document switch never flashes the
  // previous check's lines or wells, then refetch; reset the drill-ins so a
  // remount lands on the overview.
  useEffect(() => {
    if (!enabled) return;
    clearLineItems(null);
    void fetchLineItems(lineItemOverviewUrl(documentId));
    clearWells(null);
    void fetchWells(rcWellOverviewUrl(documentId));
    setSelectedWellIndex(null);
    setSelectedLineItemIndex(null);
  }, [
    enabled,
    documentId,
    clearLineItems,
    fetchLineItems,
    clearWells,
    fetchWells,
    setSelectedWellIndex,
    setSelectedLineItemIndex,
  ]);

  // Poll the well overview while a Well Matcher run is in flight.
  useWellOverviewPoll(
    rcWellOverviewUrl(documentId),
    fetchWells,
    enabled && !!wellOverview?.agentRunInProgress
  );

  return {
    overviewStatus,
    lineItemSectionStatus,
    lineItemGroups,
    selectedLineItemIndex,
    setSelectedLineItemIndex,
    selectedLineItemGroup,
    summaryAvailable,
    summaryStatus,
    wellOverview,
    wellOverviewLoading,
    wells,
    selectedWellIndex,
    setSelectedWellIndex,
    selectedWell,
    reloadWells: () => fetchWells(rcWellOverviewUrl(documentId)),
    reloadLineItems: () => fetchLineItems(lineItemOverviewUrl(documentId)),
  };
}

export const PROGRESS_LOST =
  'Could not check matching progress. Reload to try again.';

interface DocumentRerunOptions {
  rerunHref: string | undefined;
  partiesListHref: string | undefined;
  // The document-level flag from the parties list.
  agentRunInProgress: boolean;
}

/**
 * Re-run Match for the whole document: starts the run, polls the parties list while it runs (so
 * per-party spinners flip on and off), and reports a failed start or a list that can no longer be
 * reloaded.
 *
 * @param {DocumentRerunOptions} options - The rerun and list links, and the list's run flag.
 * @return {object} Whether the run is in flight, its error, and the rerun action.
 */
export function useDocumentRerun({
  rerunHref,
  partiesListHref,
  agentRunInProgress,
}: DocumentRerunOptions) {
  const startRerun = useSetAtom(rerunDocumentAtom);
  const reloadPartiesList = useSetAtom(fetchStructuredPartiesListAtom);
  // Until the start request settles the server may not have started the run,
  // so a poll that sees none must not end it.
  const starting = useRef(false);
  const list = useAtomValue(structuredPartiesListDataAtom);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const hrefRef = useLatest(partiesListHref);

  const run = useRunProgress({
    snapshot: list,
    reportedRunning: agentRunInProgress,
    scope: partiesListHref ?? '',
    pollable: !!partiesListHref,
    immediate: false,
    lostMessage: PROGRESS_LOST,
    setError: setRerunError,
    read: async (finished) => {
      const reloaded = await reloadPartiesList(hrefRef.current);
      if (!reloaded) throw new Error('The parties list could not be reloaded');
      if (!reloaded.agentRunInProgress && !starting.current) finished();
      return reloaded;
    },
  });

  /** Starts Re-run Match for the document, unless one is already running. */
  const rerun = () => {
    if (!rerunHref || run.running) return;
    setRerunError(null);
    run.start();
    starting.current = true;
    void startRerun({
      url: rerunHref,
      type: 'POST',
      data: null,
      onSuccess: () => {
        starting.current = false;
        // Refetch now so the per-party spinners don't wait for the next poll.
        void reloadPartiesList(partiesListHref).then(
          (reloaded: StructuredPartiesListResponse | undefined) => {
            if (reloaded && !reloaded.agentRunInProgress) run.finish();
          }
        );
      },
      onError: (err) => {
        starting.current = false;
        run.finish();
        setRerunError(
          actionErrorMessage(err, 'Could not re-run matching. Try again.')
        );
      },
    });
  };

  return {
    isRunning: run.running,
    rerunError,
    clearRerunError: () => setRerunError(null),
    rerun,
  };
}

// Fallback top of the scroll-spy detection band — the fixed app bar plus the
// sticky document header and tab strip — until the header has been measured.
const SCROLLSPY_TOP_OFFSET_PX = 193;

const PINNED_CHROME_VAR = '--structuring-pinned-chrome';

/**
 * The page's pinned (sticky or fixed) headers.
 *
 * @return {HTMLElement[]} The headers.
 */
const pinnedHeaders = () =>
  Array.from(window.document.querySelectorAll('header')).filter((h) => {
    const pos = window.getComputedStyle(h).position;
    return pos === 'sticky' || pos === 'fixed';
  });

/**
 * Publishes the live bottom of the pinned chrome (fixed app bar + sticky document header) as
 * --structuring-pinned-chrome, and that plus breathing room as --structuring-scroll-offset for
 * section scroll targets. The header's height is not constant: its details grid wraps by viewport
 * and document type, it has a `.scrolled` variant, and a pushing side panel narrows it until its
 * toolbar wraps onto its own row — without any window scroll or resize. So it is measured on
 * scroll and resize and whenever a pinned header changes size.
 */
export function usePinnedHeaderOffset() {
  useEffect(() => {
    const root = window.document.documentElement;
    /** Publishes the bottom of the pinned chrome as it is laid out now. */
    const measure = () => {
      let bottom = 0;
      pinnedHeaders().forEach((h) => {
        const b = h.getBoundingClientRect().bottom;
        // Only top-pinned chrome (ignore anything pushed far down the page).
        if (b > bottom && b < 500) bottom = b;
      });
      if (bottom > 0) {
        root.style.setProperty(PINNED_CHROME_VAR, `${Math.round(bottom)}px`);
        // +8px so a heading isn't flush against the header.
        root.style.setProperty(
          '--structuring-scroll-offset',
          `${Math.round(bottom) + 8}px`
        );
      }
    };
    measure();
    const resizes =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    pinnedHeaders().forEach((h) => resizes?.observe(h));
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      resizes?.disconnect();
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      root.style.removeProperty(PINNED_CHROME_VAR);
      root.style.removeProperty('--structuring-scroll-offset');
    };
  }, []);
}

/**
 * The measured bottom of the pinned chrome, or the fallback until it is measured.
 *
 * @return {number} The bottom, in px.
 */
const pinnedChromeBottom = () =>
  parseInt(
    window.document.documentElement.style.getPropertyValue(PINNED_CHROME_VAR),
    10
  ) || SCROLLSPY_TOP_OFFSET_PX;

/**
 * Scroll-spy over the Structured Overview: the anchor of the first section (in scroll order)
 * sitting just below the sticky header, or null. Sections unmount during a drill-in and remount on
 * return, so `resetKey` (anything that changes with the drill-in state) re-attaches the observer.
 *
 * @param {boolean} enabled - Whether the overview is showing; off, there is no active anchor.
 * @param {string} resetKey - Changes whenever the sections remount.
 * @return {OverviewAnchor | null} The active section's anchor.
 */
export function useOverviewScrollSpy(
  enabled: boolean,
  resetKey: string
): OverviewAnchor | null {
  const [activeAnchor, setActiveAnchor] = useState<OverviewAnchor | null>(null);
  // Turning the spy off forgets the active anchor, so turning it back on
  // starts from none until a section is seen.
  const [wasEnabled, setWasEnabled] = useState(enabled);
  if (wasEnabled !== enabled) {
    setWasEnabled(enabled);
    if (!enabled) setActiveAnchor(null);
  }

  useEffect(() => {
    if (!enabled) return undefined;
    const els = OVERVIEW_ANCHORS.map((a) =>
      window.document.getElementById(a.anchor)
    ).filter((el): el is HTMLElement => el !== null);
    if (!els.length) return undefined;

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        const top = OVERVIEW_ANCHORS.find((a) => visible.has(a.anchor));
        if (top) setActiveAnchor(top.anchor);
      },
      { rootMargin: `-${pinnedChromeBottom()}px 0px -70% 0px`, threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled, resetKey]);

  return activeAnchor;
}

const ACTIVITY_PAGE_SIZE = 25;

const ACTIVITY_FEED_QUERY_KEY = 'activityFeed';

// What a feed is filtered by; the hook adds the paging.
type ActivityFeedFilter =
  | Omit<
      ApiPayloads['documentActivityFeed']['payload']['query'],
      'page' | 'page_size' | 'sort_direction'
    >
  | Omit<
      ApiPayloads['structuredPartyActivityFeed']['payload']['query'],
      'page' | 'page_size' | 'sort_direction'
    >;

interface ActivityFeedOptions {
  path: string;
  // null while there is nothing to load.
  filter: ActivityFeedFilter | null;
  // Bumped whenever the feed's activity may have changed.
  refreshToken: number;
}

const FEED_FAILURE_FALLBACK = 'Failed to load activity log';

/**
 * The infinite query behind an activity feed: newest first, ACTIVITY_PAGE_SIZE rows a page.
 *
 * @param {string} path - The feed's query endpoint.
 * @param {ActivityFeedFilter | null} filter - What the feed is filtered by; null loads nothing.
 * @return {object} The query options.
 */
const activityFeedQueryOptions = (
  path: string,
  filter: ActivityFeedFilter | null
) =>
  infiniteQueryOptions({
    queryKey: [ACTIVITY_FEED_QUERY_KEY, path, filter],
    queryFn: async ({ pageParam, signal }) => {
      try {
        return await api
          .post(path, {
            json: {
              query: {
                ...filter,
                page: pageParam,
                page_size: ACTIVITY_PAGE_SIZE,
                sort_direction: 'desc',
              },
            },
            signal,
          })
          .json<AuditFeedResponse>();
      } catch (error) {
        if (signal.aborted) throw error;
        // A 5xx's body is no message for the examiner.
        const serverFault =
          error instanceof HTTPError && error.response.status >= 500;
        throw new Error(
          serverFault
            ? FEED_FAILURE_FALLBACK
            : await apiErrorMessage(error, FEED_FAILURE_FALLBACK),
          { cause: error }
        );
      }
    },
    initialPageParam: 1,
    getNextPageParam: (last, all) =>
      all.length * ACTIVITY_PAGE_SIZE < last.totalCount
        ? all.length + 1
        : undefined,
    enabled: filter !== null,
    retry: false,
    // A revisit starts from the newest page rather than refetching every page it had loaded.
    gcTime: 0,
  });

/**
 * Loads an activity feed newest first, a page at a time. A refresh collapses it back to the newest
 * page, and a later page whose total differs from the newest page's (events added or removed since)
 * starts it again from the newest page, keeping the newest page shown meanwhile.
 *
 * @param {ActivityFeedOptions} options - The feed's path, filter and refresh token.
 * @return {object} The loaded rows, the feed's total, its load state, and the next-page loader.
 */
function useActivityFeed({ path, filter, refreshToken }: ActivityFeedOptions) {
  const queryClient = useQueryClient();
  // Built from the filter's contents, so a caller needn't keep its filter object stable.
  const feedKey = JSON.stringify(filter);
  const options = useMemo(
    () =>
      activityFeedQueryOptions(
        path,
        JSON.parse(feedKey) as ActivityFeedFilter | null
      ),
    [path, feedKey]
  );
  const { queryKey } = options;
  const query = useInfiniteQuery(options);

  // Keeps the newest page on screen while it reloads, dropping the older pages. Before the first
  // page has landed, a reset replaces its request, which may predate the change.
  const restartFromNewestPage = useCallback(() => {
    if (queryClient.getQueryData(queryKey) === undefined) {
      void queryClient.resetQueries({ queryKey, exact: true });
      return;
    }
    queryClient.setQueryData<InfiniteData<AuditFeedResponse, number>>(
      queryKey,
      (data) =>
        data && {
          pages: data.pages.slice(0, 1),
          pageParams: data.pageParams.slice(0, 1),
        }
    );
    void queryClient.refetchQueries({ queryKey, exact: true });
  }, [queryClient, queryKey]);

  const shownToken = useRef(refreshToken);
  useEffect(() => {
    if (shownToken.current === refreshToken) return;
    shownToken.current = refreshToken;
    restartFromNewestPage();
  }, [restartFromNewestPage, refreshToken]);

  const pages = query.data?.pages ?? [];
  const totalCount = pages[0]?.totalCount ?? 0;
  // Pages after one whose total moved are from a shifted feed; only the pages before it are shown.
  const firstShifted = pages.findIndex(
    (page) => page.totalCount !== totalCount
  );
  const shown = firstShifted === -1 ? pages : pages.slice(0, firstShifted);
  const shifted = firstShifted !== -1;

  useEffect(() => {
    if (shifted) restartFromNewestPage();
  }, [restartFromNewestPage, shifted]);

  const rows = useMemo(() => {
    const seen = new Set<string>();
    return shown
      .flatMap((page) => page.results ?? [])
      .filter((row) => {
        if (seen.has(row.activityId)) return false;
        seen.add(row.activityId);
        return true;
      });
  }, [shown]);

  const remaining = Math.max(0, totalCount - rows.length);
  // Paging by count alone could offer a page past the end once rows shift.
  const hasMore =
    remaining > 0 && shown.length * ACTIVITY_PAGE_SIZE < totalCount;
  const { fetchNextPage, refetch } = query;
  const restartFailed = query.isRefetchError;
  const loadMore = useCallback(() => {
    void (restartFailed ? refetch() : fetchNextPage());
  }, [fetchNextPage, refetch, restartFailed]);

  return {
    rows,
    totalCount,
    loading: query.isLoading,
    error: query.isError && !query.data ? query.error.message : null,
    // A refetch with rows shown is a restart; paging on during one would cancel it.
    loadingMore: query.isFetchingNextPage || query.isRefetching,
    loadMoreFailed: query.isFetchNextPageError || restartFailed,
    nextPageCount: hasMore ? Math.min(ACTIVITY_PAGE_SIZE, remaining) : 0,
    loadMore,
  };
}

export type ActivityFeed = ReturnType<typeof useActivityFeed>;

interface PartyActivityFeedOptions {
  documentId: number;
  party: Pick<StructuredPartyDetail, 'id' | 'kind'> | null;
  // Bumped whenever the party's activity may have changed.
  refreshToken: number;
}

/**
 * Loads a party's activity feed a page at a time (see useActivityFeed).
 *
 * @param {PartyActivityFeedOptions} options - The document, the party, and the refresh token.
 * @return {ActivityFeed} The party's feed.
 */
export function usePartyActivityFeed({
  documentId,
  party,
  refreshToken,
}: PartyActivityFeedOptions): ActivityFeed {
  return useActivityFeed({
    path: `documents/${documentId}/queries/structured_party_activity_feeds`,
    filter: party ? { kind: party.kind, id: party.id } : null,
    refreshToken,
  });
}

interface DocumentActivityFeedOptions {
  documentId: number;
  // Bumped whenever the document's activity may have changed.
  refreshToken: number;
}

/**
 * Loads a document's activity feed a page at a time (see useActivityFeed).
 *
 * @param {DocumentActivityFeedOptions} options - The document and the refresh token.
 * @return {ActivityFeed} The document's feed.
 */
export function useDocumentActivityFeed({
  documentId,
  refreshToken,
}: DocumentActivityFeedOptions): ActivityFeed {
  return useActivityFeed({
    path: `documents/${documentId}/queries/document_activity_feeds`,
    filter: {},
    refreshToken,
  });
}

/**
 * Query options for an agent run's prompt, read from the endpoint its feed row links.
 *
 * @param {string} href - The run's prompt endpoint.
 * @return {object} The prompt's query options.
 */
export function agentRunPromptOptions(href: string) {
  return queryOptions({
    queryKey: [...QUERY_KEY, 'agent-run-prompt', href],
    queryFn: ({ signal }) =>
      api.get(apiPath(href), { signal }).json<{ prompt: string | null }>(),
    staleTime: Infinity,
  });
}

/**
 * An agent run's prompt, loaded only once `enabled` (its section is open).
 *
 * @param {string} href - The run's prompt endpoint.
 * @param {boolean} enabled - Whether to load it.
 * @return {object} The prompt query.
 */
export function useAgentRunPrompt(href: string, enabled: boolean) {
  return useQuery({ ...agentRunPromptOptions(href), enabled });
}

const DISCARDED = 'Your unsaved selection was discarded.';
const BY_ANOTHER_EXAMINER = `Another examiner updated this party. ${DISCARDED}`;
const CHANGED_SINCE_OPENED = `This party changed since you opened it. ${DISCARDED}`;
const RESOLVED_SINCE_OPENED = `This party was resolved while you had it open. ${DISCARDED}`;

export interface PartyChannelPayload {
  clientSessionId?: string;
}

// The document-channel pushes that change a party.
export const PARTY_PUSH_EVENTS = [
  'structured-party-changed',
  'reconcile-completed',
  'reconcile-undone',
] as const;

/**
 * Whether this tab's own action caused the push; that action already refreshed.
 *
 * @param {PartyChannelPayload} data - The push.
 * @return {boolean} True for this tab's own push.
 */
export const isOwnPush = (data?: PartyChannelPayload): boolean =>
  data?.clientSessionId === CLIENT_SESSION_ID;

/**
 * Whether Auto-match newly confirmed the party between two reads of it.
 *
 * @param {StructuredPartyDetail} before - The detail on screen.
 * @param {StructuredPartyDetail} after - The fresh detail.
 * @return {boolean} True for a new auto-confirmation.
 */
const autoConfirmedAnew = (
  before: StructuredPartyDetail,
  after: StructuredPartyDetail
) =>
  after.autoMatch?.outcome === 'auto_confirmed' &&
  !isEqual(before.autoMatch, after.autoMatch);

/**
 * The notice for a resolution Auto-match made.
 *
 * @param {StructuredPartyDetail} fresh - The fresh detail.
 * @return {string} The notice.
 */
const autoMatchMessage = (fresh: StructuredPartyDetail) => {
  const name = fresh.confirmedEntity?.name ?? fresh.currentAnchor?.name;
  return name
    ? `Auto-match matched this party to ${endSentence(name)} ${DISCARDED}`
    : `Auto-match updated this party. ${DISCARDED}`;
};

/**
 * Whether the party's resolution differs between two reads of it.
 *
 * @param {StructuredPartyDetail} before - The detail on screen.
 * @param {StructuredPartyDetail} after - The fresh detail.
 * @return {boolean} True when its status or anchor changed.
 */
const resolutionChanged = (
  before: StructuredPartyDetail,
  after: StructuredPartyDetail
) =>
  before.status !== after.status ||
  before.currentAnchor?.id !== after.currentAnchor?.id;

// What the parties list shows of a party.
const LIST_FIELD_FLAGS: Record<keyof StructuredPartyListItem, true> = {
  href: true,
  auth: true,
  id: true,
  kind: true,
  partyNumber: true,
  name: true,
  role: true,
  status: true,
  partyType: true,
  confirmedBy: true,
  stagedEntity: true,
  confirmedEntity: true,
  currentAnchor: true,
  unresolvable: true,
  arrivalConfidence: true,
  pendingSuggestionsCount: true,
  noMatchSuggestion: true,
  resolvedViaDedupe: true,
  resolvedViaReconcile: true,
  agentRunInProgress: true,
  matchRunInProgress: true,
  addressParseInProgress: true,
  autoMatch: true,
  resources: true,
};
const LIST_FIELDS = Object.keys(
  LIST_FIELD_FLAGS
) as (keyof StructuredPartyListItem)[];

/**
 * Whether anything the parties list shows differs between two reads of a party.
 *
 * @param {StructuredPartyDetail} before - The detail on screen.
 * @param {StructuredPartyDetail} after - The fresh detail.
 * @return {boolean} True when a list field changed.
 */
const listFieldsChanged = (
  before: StructuredPartyDetail,
  after: StructuredPartyDetail
) => LIST_FIELDS.some((key) => !isEqual(before[key], after[key]));

/**
 * A fresh record of the checks running for one party.
 *
 * @return {object} No check in flight, none queued, none for a change no push announced.
 */
const newBurst = () => ({ inFlight: false, queued: false, unannounced: false });

/**
 * Whether a value from the untyped `lib/pusher` is a channel that can be bound and unbound.
 *
 * @param {unknown} value - What `subscribe` returned.
 * @return {boolean} True for a channel.
 */
const isJobChannel = (value: unknown): value is JobChannel =>
  typeof value === 'object' &&
  value !== null &&
  'bind' in value &&
  'unbind' in value &&
  typeof value.bind === 'function' &&
  typeof value.unbind === 'function';

interface PartyLiveUpdatesOptions {
  channelName: string | undefined;
  selfHref: string;
  detail: StructuredPartyDetail | null;
  detailLoading: boolean;
  // Any of this session's own actions on the party is in flight.
  ownSubmitInFlight: boolean;
  // The party's resolution changed under the examiner. `fresh` is the checked
  // detail; the message is built only if it is shown. `listStale` is whether
  // the parties list needs refreshing here: the page refreshes it on every
  // push, so only for a change no push announced.
  onResolutionChanged: (
    fresh: StructuredPartyDetail,
    message: () => string,
    listStale: boolean
  ) => void;
  // Something about the party changed, or the check failed; its resolution
  // did not. `fresh` is the checked detail (null when the check failed).
  onRefresh: (fresh: StructuredPartyDetail | null, listStale: boolean) => void;
}

/**
 * Keeps the open party in step with changes made elsewhere. A resolution can land on it from
 * Auto-Match or another examiner, including through a different party (or document) sharing its
 * anchor. Every push other than this tab's own is checked against a fresh detail: only a changed
 * resolution discards the examiner's open work. A burst of pushes is one check at a time, and a
 * push that lands while the detail reloads waits for the reload, so it is compared against the
 * settled party. Who made the change is read from the fresh detail, never from the push.
 *
 * @param {PartyLiveUpdatesOptions} options - The party, its channel and what to do on a change.
 */
export function usePartyLiveUpdates(options: PartyLiveUpdatesOptions) {
  const readPartyDetail = useReadPartyDetail();
  const burst = useRef(newBurst());
  const latest = useRef(options);
  useLayoutEffect(() => {
    // The view is reused across parties: a check or held push for the previous
    // party says nothing about this one.
    if (latest.current.selfHref !== options.selfHref)
      burst.current = newBurst();
    latest.current = options;
  });

  // Counts this session's submits, so a check that overlapped one leaves the
  // outcome to that submit's own refresh.
  const ownSubmits = useRef(0);
  useEffect(() => {
    if (options.ownSubmitInFlight) ownSubmits.current += 1;
  }, [options.ownSubmitInFlight]);

  // Bumped when a check settles with a push still held, so the held push runs
  // after the render that applies the check's outcome, against that party.
  const [settled, setSettled] = useState(0);

  const check = useCallback(() => {
    const run = burst.current;
    const { selfHref, detail, detailLoading } = latest.current;
    if (!detail || detailLoading || run.inFlight) {
      run.queued = true;
      return;
    }
    run.inFlight = true;
    run.queued = false;
    const { unannounced } = run;
    run.unannounced = false;
    const submitsAtStart = ownSubmits.current;
    void readPartyDetail(selfHref)
      .catch(() => null)
      .then((fresh) => {
        if (burst.current !== run) return;
        run.inFlight = false;
        const now = latest.current;
        if (now.ownSubmitInFlight || ownSubmits.current !== submitsAtStart) {
          run.queued = false;
          return;
        }
        if (fresh && resolutionChanged(detail, fresh)) {
          // Pushes held behind this check describe the change it found.
          run.queued = false;
          now.onResolutionChanged(
            fresh,
            () => {
              if (autoConfirmedAnew(detail, fresh))
                return autoMatchMessage(fresh);
              return BY_ANOTHER_EXAMINER;
            },
            unannounced
          );
          return;
        }
        // A newer detail landed meanwhile, or a reload is still in flight (whose
        // failure restores the older snapshot over `fresh`): check again once
        // it settles.
        if (now.detail !== detail || now.detailLoading) {
          run.queued = true;
          run.unannounced ||= unannounced;
        } else if (!fresh || !isEqual(detail, fresh)) {
          now.onRefresh(
            fresh,
            unannounced && (!fresh || listFieldsChanged(detail, fresh))
          );
        }
        if (run.queued) setSettled((n) => n + 1);
      });
  }, [readPartyDetail]);

  // A held push runs once the reload settles, or once the render that applied
  // an in-place swap has landed.
  useEffect(() => {
    if (!options.detailLoading && burst.current.queued) check();
  }, [options.detailLoading, settled, check]);

  /**
   * Checks the party on a push about it, other than this tab's own.
   *
   * @param {PartyChannelPayload} data - The push.
   */
  const handleChange = (data?: PartyChannelPayload) => {
    if (!isOwnPush(data)) check();
  };

  useDocumentChannelEvents(
    options.channelName,
    Object.fromEntries(PARTY_PUSH_EVENTS.map((name) => [name, handleChange]))
  );

  // The channel is shared with the rest of the page, so it is armed rather
  // than owned: a change published before the subscription registered is
  // caught by the check its acknowledgement runs.
  const { channelName } = options;
  useEffect(() => {
    if (!channelName) return undefined;
    const channel: unknown = subscribe(channelName);
    return armJobCompletion({
      channel: isJobChannel(channel) ? channel : null,
      reconcile: () => {
        burst.current.unannounced = true;
        check();
      },
    });
  }, [channelName, check]);
}

const NOTIFY_TITLES = {
  success: 'Success',
  error: 'Error',
  info: 'Party updated',
} as const;

const PARTY_PROGRESS_LOST =
  'We lost track of this re-run. Retry to check again.';

// The server's refusal of an action on a party that moved since it was shown.
const PARTY_REFUSAL_CODE = 'ANCHOR_RESOLVED';

type NotifyKind = keyof typeof NOTIFY_TITLES;
type Notify = (id: string, message: string, kind?: NotifyKind) => void;
type ReportError = (err?: StructuredPartyActionError) => void;
type ForThisParty = <Args extends unknown[]>(
  callback: (...args: Args) => void,
  otherwise?: (...args: NoInfer<Args>) => void
) => (...args: Args) => void;

/**
 * The side panels a party opens (glossary, agent details, change match, canonize, staged editor,
 * reconcile), with their toggles.
 *
 * @return {object} Each panel's open flag and controls, and `closePartyPanels`.
 */
function usePartyPanels() {
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [agentDetailsOpen, setAgentDetailsOpen] = useState(false);
  const [changeMatchOpen, setChangeMatchOpen] = useState(false);
  const [canonizeOpen, setCanonizeOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [stagedEditorOpen, setStagedEditorOpen] = useState(false);

  // The side panels act on this party. Close them when the viewed party changes
  // (so one opened for a party never re-targets the next) and when a mutation
  // resolves the party (which can hide the actions that opened them).
  /** Closes every side panel. */
  const closePartyPanels = () => {
    setChangeMatchOpen(false);
    setCanonizeOpen(false);
    setStagedEditorOpen(false);
    setReconcileOpen(false);
    setAgentDetailsOpen(false);
  };

  return {
    closePartyPanels,
    panels: {
      glossaryOpen,
      toggleGlossary: () => setGlossaryOpen((v) => !v),
      closeGlossary: () => setGlossaryOpen(false),
      agentDetailsOpen,
      toggleAgentDetails: () => setAgentDetailsOpen((v) => !v),
      closeAgentDetails: () => setAgentDetailsOpen(false),
      changeMatchOpen,
      openChangeMatch: () => setChangeMatchOpen(true),
      toggleChangeMatch: () => setChangeMatchOpen((v) => !v),
      closeChangeMatch: () => setChangeMatchOpen(false),
      canonizeOpen,
      openCanonize: () => setCanonizeOpen(true),
      closeCanonize: () => setCanonizeOpen(false),
      stagedEditorOpen,
      openStagedEditor: () => setStagedEditorOpen(true),
      closeStagedEditor: () => setStagedEditorOpen(false),
      reconcileOpen,
      openReconcile: () => setReconcileOpen(true),
      closeReconcile: () => setReconcileOpen(false),
      closeAll: closePartyPanels,
    },
  };
}

/**
 * The transient banners shown after an action on the current party view: the reconcile result,
 * the stale-confidence notice and the alias notice after Mark as Correct.
 *
 * @return {object} The banners' state, setters and `clearTransientBanners`.
 */
function usePartyBanners() {
  const [confidenceStale, setConfidenceStale] = useState(false);
  const [reconcileResult, setReconcileResult] =
    useState<ReconcileCreateResponse | null>(null);
  const [reconcileDetailsOpen, setReconcileDetailsOpen] = useState(false);
  // Shown right after a Mark as Correct that resolved via an alias.
  const [markCorrectAlias, setMarkCorrectAlias] = useState(false);

  /** Hides every post-action banner. */
  const clearTransientBanners = () => {
    setReconcileResult(null);
    setReconcileDetailsOpen(false);
    setConfidenceStale(false);
    setMarkCorrectAlias(false);
  };

  return {
    setConfidenceStale,
    setReconcileResult,
    setReconcileDetailsOpen,
    setMarkCorrectAlias,
    clearTransientBanners,
    banners: {
      reconcileResult,
      reconcileDetailsOpen,
      toggleReconcileDetails: () => setReconcileDetailsOpen((v) => !v),
      markCorrectAlias,
      confidenceStale,
      dismissConfidenceStale: () => setConfidenceStale(false),
    },
  };
}

/**
 * The party's match run (see useRunProgress), read from its detail. A finished run refreshes
 * the party only while it is still the one shown.
 *
 * @param {object} options - The party.
 * @param {string} options.selfHref - The party's href.
 * @param {StructuredPartyDetail | null} options.detail - The loaded detail.
 * @param {ForThisParty} options.forThisParty - Scopes a callback to the party it started for.
 * @param {Function} options.onFinished - Refreshes what a finished run changed.
 * @param {Function} options.setError - Shows the view's error.
 * @return {object} Whether the run is pending, whether its progress was lost, and its controls.
 */
function usePartyRun({
  selfHref,
  detail,
  forThisParty,
  onFinished,
  setError,
}: {
  selfHref: string;
  detail: StructuredPartyDetail | null;
  forThisParty: ForThisParty;
  onFinished: () => void;
  setError: (update: (current: string | null) => string | null) => void;
}) {
  const readPartyDetail = useReadPartyDetail();
  const run = useRunProgress({
    snapshot: detail,
    reportedRunning: !!detail?.matchRunInProgress,
    scope: selfHref,
    immediate: true,
    lostMessage: PARTY_PROGRESS_LOST,
    setError,
    read: async (finished) => {
      const report = forThisParty(() => {
        finished();
        onFinished();
      });
      const fresh = await readPartyDetail(selfHref);
      if (!fresh.matchRunInProgress) report();
      return fresh;
    },
  });

  return {
    rerunPending: run.running,
    progressLost: run.progressLost,
    startRun: run.start,
    resetForParty: run.reset,
  };
}

/**
 * The submit states of every action on a party, from its atoms.
 *
 * @return {object} The triggers this hook's actions call, `pending` (one of them is in flight)
 *   and `ownSubmitInFlight` (any of this session's submits on the party is).
 */
function usePartySubmits() {
  const [confirmState, triggerConfirm] = useAtom(confirmSuggestionAtom);
  const [revertState, triggerRevert] = useAtom(revertSuggestionAtom);
  const [removeState, triggerRemove] = useAtom(removePartyAtom);
  const [restoreState, triggerRestore] = useAtom(restorePartyAtom);
  const [rerunState, triggerRerun] = useAtom(rerunPartyAtom);
  const [reconcileUndoState, triggerReconcileUndo] = useAtom(reconcileUndoAtom);
  // The panels and tearsheet own these submits; they count as this session's.
  const canonizeState = useAtomValue(canonizePartyAtom);
  const userSelectionState = useAtomValue(saveUserSelectionAtom);
  const reconcileCreateState = useAtomValue(reconcileCreateAtom);
  const updateDetailsState = useAtomValue(updatePartyDetailsAtom);

  const pending =
    confirmState.loading ||
    revertState.loading ||
    removeState.loading ||
    restoreState.loading ||
    rerunState.loading ||
    reconcileUndoState.loading;
  const ownSubmitInFlight =
    pending ||
    canonizeState.loading ||
    userSelectionState.loading ||
    reconcileCreateState.loading ||
    updateDetailsState.loading;

  return {
    triggers: {
      confirm: triggerConfirm,
      revert: triggerRevert,
      remove: triggerRemove,
      restore: triggerRestore,
      rerun: triggerRerun,
      reconcileUndo: triggerReconcileUndo,
    },
    pending,
    ownSubmitInFlight,
    confirmLoading: confirmState.loading,
  };
}

type PartySubmits = ReturnType<typeof usePartySubmits>;
type PartyModals = ReturnType<typeof usePartyModals>;
type PartyBanners = ReturnType<typeof usePartyBanners>;

/** What the action handlers of a loaded party share. */
interface PartyHandlerContext {
  document: DocumentResponse;
  detail: StructuredPartyDetail;
  triggers: PartySubmits['triggers'];
  modals: PartyModals;
  banners: PartyBanners;
  closePartyPanels: () => void;
  notify: Notify;
  afterMutation: () => void;
  refreshActivity: () => void;
  onMutated: () => void;
  forThisParty: ForThisParty;
  outcomes: PartyOutcomes;
  setError: (error: string | null) => void;
  startRun: () => void;
  discardOpenWork: () => boolean;
}

/** A toast for an outcome: shown here, and, naming the party, after a switch. */
interface OutcomeToast {
  id: string;
  message: string;
  // Shown here instead of `message`; false shows nothing here.
  here?: string | false;
}

/**
 * A request's callbacks, scoped to the party it started for (see useForThisParty). After a switch,
 * a success refreshes the party shown and says so, naming this party, and a failure is a toast.
 */
interface PartyOutcomes {
  succeeded: <Args extends unknown[]>(
    toast: OutcomeToast,
    here?: (...args: Args) => void
  ) => (...args: Args) => void;
  changed: <Args extends unknown[]>(
    here: (...args: Args) => void
  ) => (...args: Args) => void;
  // `closeConfirm` closes the action's confirm on the server's refusal of a
  // party that moved; any other failure shows inside it, keeping its input.
  failed: (fallback: string, closeConfirm?: () => void) => ReportError;
}

/**
 * Mark as Correct: confirms the selected suggestion, deduplicating by default or keeping the
 * staged record as an alias, and asks for an override when a flagged entity is involved.
 *
 * @param {PartyHandlerContext} ctx - The loaded party's shared context.
 * @param {number | null} selectedSuggestionId - The suggestion to confirm.
 * @return {Function} The handler, taking the override rationale and false positives.
 */
function confirmHandler(
  ctx: PartyHandlerContext,
  selectedSuggestionId: number | null
) {
  const { detail, modals, banners, notify, outcomes } = ctx;
  const partyLabel = `Party ${detail.partyNumber}`;
  /** Closes Mark as Correct and the consolidation-risk override. */
  const closeConfirm = () => {
    modals.setFlaggedForOverride(null);
    modals.closeMarkCorrect();
  };
  const failed = outcomes.failed('Could not confirm match', closeConfirm);
  return (overrideRationale?: string, falsePositiveIds?: number[]) => {
    if (!selectedSuggestionId) return;
    ctx.setError(null);
    modals.setDialogError(null);
    void ctx.triggers.confirm({
      url: partyUrl(ctx.document.id, detail.id, detail.kind, '/confirmation'),
      type: 'POST',
      data: {
        suggestion_id: selectedSuggestionId,
        // Dedupe by default; alias only when the staged record is explicitly
        // preserved, in which case the reason is required.
        preserve_staged: modals.preserveStaged,
        ...(modals.preserveStaged
          ? { alias_rationale: modals.aliasReason.trim() }
          : {}),
        ...(overrideRationale ? { override_rationale: overrideRationale } : {}),
        ...(falsePositiveIds?.length
          ? { false_positive_entity_ids: falsePositiveIds }
          : {}),
        client_session_id: CLIENT_SESSION_ID,
      },
      onSuccess: outcomes.succeeded(
        { id: 'confirmed', message: 'Match updated successfully' },
        (data) => {
          closeConfirm();
          banners.clearTransientBanners();
          banners.setMarkCorrectAlias(
            data?.outcome === 'alias_only' || data?.outcome === 'mixed'
          );
          ctx.closePartyPanels();
          ctx.afterMutation();
        }
      ),
      onError: (err) => {
        // A flagged staged/matched entity prompts for an override rationale
        // rather than failing outright.
        if (!isFlagOverrideRequired(err)) {
          failed(err);
          return;
        }
        ctx.forThisParty(
          () => modals.setFlaggedForOverride(err.flaggedEntities),
          () =>
            notify(
              'failed',
              `${partyLabel}: Mark as Correct needs a consolidation-risk override. Reopen the party to confirm it.`,
              'error'
            )
        )();
      },
    });
  };
}

/**
 * The reconcile handlers: recording a completed reconcile, undoing the last one, and closing the
 * tearsheet when the server refused it over a changed resolution. Undo is session-scoped: only the
 * in-session stack reverses a reconcile; once the session ends (refresh), the reconcile is
 * committed and is reversed from the Entities screen, like any dedupe merge.
 *
 * @param {PartyHandlerContext} ctx - The loaded party's shared context.
 * @param {object} undo - The party's in-session undo stack.
 * @param {string} reconcileSession - The working-session token, a server-enforced undo guard.
 * @return {object} `handleReconcileComplete`, `handleReconcileUndo` and
 *   `handleReconcileResolutionChanged`.
 */
function reconcileHandlers(
  ctx: PartyHandlerContext,
  undo: ReturnType<typeof partyUndoStack>,
  reconcileSession: string
) {
  const { detail, banners, outcomes } = ctx;
  return {
    handleReconcileComplete: (result: ReconcileCreateResponse) => {
      undo.push(result);
      outcomes.succeeded(
        { id: 'reconciled', message: reconcileOutcomeLabel(result.outcome) },
        () => {
          banners.setReconcileResult(result);
          banners.setReconcileDetailsOpen(false);
          banners.setConfidenceStale(result.confidenceStale);
          ctx.closePartyPanels();
          ctx.afterMutation();
        }
      )();
    },
    handleReconcileUndo: () => {
      const top = undo.stack[undo.stack.length - 1];
      if (!top) return;
      const succeeded = outcomes.succeeded(
        { id: 'reconcile-undone', message: 'Reconcile undone' },
        () => {
          banners.clearTransientBanners();
          ctx.afterMutation();
        }
      );
      ctx.setError(null);
      void ctx.triggers.reconcileUndo({
        url: reconcileUndoUrl(ctx.document.id, detail.id, top.reconciliationId),
        type: 'POST',
        data: {
          session_id: reconcileSession,
          client_session_id: CLIENT_SESSION_ID,
        },
        onSuccess: () => {
          undo.pop();
          succeeded();
        },
        onError: outcomes.failed('Could not undo the reconcile'),
      });
    },
    // The server refused a reconcile because the party's resolution changed
    // since the tearsheet opened: close it as a push would, saying whether the
    // party was resolved or only moved.
    handleReconcileResolutionChanged: outcomes.changed(
      (reason?: ResolutionChangeReason) => {
        ctx.discardOpenWork();
        ctx.notify(
          'updated-elsewhere',
          reason === 'resolved' ? RESOLVED_SINCE_OPENED : CHANGED_SINCE_OPENED,
          'info'
        );
        ctx.afterMutation();
      }
    ),
  };
}

/**
 * The handlers that change the party's standing: Remove Correct Status, Remove, Restore and
 * Re-run.
 *
 * @param {PartyHandlerContext} ctx - The loaded party's shared context.
 * @return {object} `handleRevert`, `handleRemove`, `handleRestore` and `handleRerun`.
 */
function standingHandlers(ctx: PartyHandlerContext) {
  const { document, detail, modals, banners, outcomes } = ctx;
  return {
    handleRevert: () => {
      modals.setRevertOpen(false);
      ctx.setError(null);
      void ctx.triggers.revert({
        url: partyUrl(document.id, detail.id, detail.kind, '/confirmation'),
        type: 'DELETE',
        data: { client_session_id: CLIENT_SESSION_ID },
        onSuccess: outcomes.succeeded(
          { id: 'reverted', message: 'Correct status removed', here: false },
          () => {
            banners.clearTransientBanners();
            ctx.afterMutation();
          }
        ),
        onError: outcomes.failed('Could not remove correct status'),
      });
    },
    handleRemove: () => {
      ctx.setError(null);
      modals.setDialogError(null);
      void ctx.triggers.remove({
        url: partyUrl(document.id, detail.id, detail.kind),
        type: 'DELETE',
        data: { client_session_id: CLIENT_SESSION_ID },
        onSuccess: outcomes.succeeded(
          { id: 'removed', message: 'Party removed' },
          () => {
            modals.setDeleteOpen(false);
            ctx.closePartyPanels();
            ctx.afterMutation();
          }
        ),
        onError: outcomes.failed('Could not remove party', () =>
          modals.setDeleteOpen(false)
        ),
      });
    },
    handleRestore: () => {
      ctx.setError(null);
      void ctx.triggers.restore({
        url: partyUrl(document.id, detail.id, detail.kind, '/restoration'),
        type: 'POST',
        data: { client_session_id: CLIENT_SESSION_ID },
        onSuccess: outcomes.succeeded(
          { id: 'restored', message: 'Party restored', here: false },
          ctx.afterMutation
        ),
        onError: outcomes.failed('Could not restore party'),
      });
    },
    handleRerun: () => {
      modals.setRerunOpen(false);
      ctx.setError(null);
      void ctx.triggers.rerun({
        url: partyUrl(document.id, detail.id, detail.kind, '/suggestion_rerun'),
        type: 'POST',
        data: { client_session_id: CLIENT_SESSION_ID },
        onSuccess: outcomes.succeeded(
          {
            id: 'rerun',
            message: 'Re-run started',
            here: 'Re-run started — generating fresh suggestions…',
          },
          () => {
            ctx.startRun();
            // A re-run regenerates confidence, so the stale banner no longer applies.
            banners.clearTransientBanners();
            ctx.refreshActivity();
            // The parties list shows the per-party spinner right away.
            ctx.onMutated();
          }
        ),
        onError: outcomes.failed('Could not re-run matching'),
      });
    },
  };
}

/**
 * The in-session reconcile undo stack of one party. It is keyed by party, so it is kept in step
 * even when a request finishes after a switch.
 *
 * @param {number} partyId - The party's id.
 * @param {object} stacks - Every party's stack, and their setter.
 * @return {object} The party's stack, with push, pop and clear.
 */
function partyUndoStack(
  partyId: number,
  [stacks, setStacks]: ReturnType<typeof useReconcileUndoStacks>
) {
  return {
    stack: stacks[partyId] ?? [],
    push: (result: ReconcileCreateResponse) =>
      setStacks((prev) => ({
        ...prev,
        [partyId]: [
          ...(prev[partyId] ?? []),
          {
            reconciliationId: result.reconciliationId,
            summary: reconcileOutcomeLabel(result.outcome),
          },
        ],
      })),
    pop: () =>
      setStacks((prev) => ({
        ...prev,
        [partyId]: (prev[partyId] ?? []).slice(0, -1),
      })),
  };
}

/**
 * Every party's in-session reconcile undo stack.
 *
 * @return {Array} The stacks and their setter.
 */
function useReconcileUndoStacks() {
  return useAtom(reconcileUndoStackAtom);
}

interface PartyActionsOptions {
  document: DocumentResponse;
  selfHref: string;
  detail: StructuredPartyDetail | null;
  validMatches: SuggestionSummary[];
  refetchDetail: () => void;
  onMutated: () => void;
}

/**
 * Raises toasts in the structured-parties group.
 *
 * @return {Notify} Raises one toast: its id prefix, message and kind (which sets its title).
 */
function usePartyNotify(): Notify {
  const addNotifications = useSetAtom(addNotificationsAtom);
  return useCallback(
    (id, message, kind = 'success') =>
      addNotifications(NotificationGroup.StructuredParties, [
        {
          id: `${id}-${Date.now()}`,
          title: NOTIFY_TITLES[kind],
          message,
          kind,
          autoHideMs: 5000,
        },
      ]),
    [addNotifications]
  );
}

/**
 * Scopes a request's callbacks to the party it started for. A request started for one party can
 * finish after the examiner has moved to another. Its callbacks must then leave this view alone
 * (they would close the new party's panels, or open the flag-override modal against it);
 * `otherwise` still reports the outcome.
 *
 * @param {object} options - The party.
 * @param {string} options.selfHref - The party's href.
 * @param {Function} options.refetchDetail - Refetches the party.
 * @param {Function} options.onMutated - Refreshes the parties list.
 * @return {object} `forThisParty` and `refreshViewed`.
 */
function useForThisParty({
  selfHref,
  refetchDetail,
  onMutated,
}: {
  selfHref: string;
  refetchDetail: () => void;
  onMutated: () => void;
}) {
  const viewedHrefRef = useLatest(selfHref);
  const refetchViewedRef = useLatest(refetchDetail);
  /**
   * Wraps a request's callback so it runs only while its party is still shown.
   *
   * @param {Function} callback - Runs while the party is shown.
   * @param {Function} otherwise - Runs once the examiner has moved on.
   * @return {Function} The wrapped callback.
   */
  const forThisParty: ForThisParty = (callback, otherwise) => {
    const startedFor = selfHref;
    return (...args) => {
      if (viewedHrefRef.current === startedFor) callback(...args);
      else otherwise?.(...args);
    };
  };
  // What a request reports when it lands after a switch: the parties list and
  // the party now shown refresh (it may share the anchor, and this tab's own
  // push is ignored).
  const refreshViewed = useCallback(() => {
    onMutated();
    refetchViewedRef.current();
  }, [onMutated, refetchViewedRef]);
  return { forThisParty, refreshViewed };
}

/**
 * What a request for a party reports. When it lands after a switch, the parties list and the party
 * now shown refresh (it may share the anchor, and this tab's own push is ignored), and a toast
 * names the party.
 *
 * @param {StructuredPartyDetail} detail - The party the request started for.
 * @param {object} report - Where outcomes go: `forThisParty`, `refreshViewed`, `notify`,
 *   `setError` (the party view's error) and `setDialogError` (the open confirm's).
 * @return {PartyOutcomes} The callbacks.
 */
function partyOutcomes(
  detail: StructuredPartyDetail,
  {
    forThisParty,
    refreshViewed,
    notify,
    setError,
    setDialogError,
  }: {
    forThisParty: ForThisParty;
    refreshViewed: () => void;
    notify: Notify;
    setError: (message: string) => void;
    setDialogError: (message: string) => void;
  }
): PartyOutcomes {
  const partyLabel = `Party ${detail.partyNumber}`;
  return {
    succeeded: ({ id, message, here = message }, then) =>
      forThisParty(
        (...args) => {
          if (here !== false) notify(id, here);
          then?.(...args);
        },
        () => {
          refreshViewed();
          notify(id, `${partyLabel}: ${message}`);
        }
      ),
    changed: (here) => forThisParty(here, refreshViewed),
    failed: (fallback, closeConfirm) =>
      forThisParty(
        (err) => {
          const message = actionErrorMessage(err, fallback);
          if (closeConfirm && err?.code !== PARTY_REFUSAL_CODE) {
            setDialogError(message);
            return;
          }
          closeConfirm?.();
          setError(message);
        },
        (err) =>
          notify(
            'failed',
            `${partyLabel}: ${actionErrorMessage(err, fallback)}`,
            'error'
          )
      ),
  };
}

/**
 * The Match Review actions on one party, with the modal, panel and banner state they open and
 * close. The selection, the running indicator and the polling are derived from the loaded detail
 * plus what the examiner did in this view.
 *
 * @param {PartyActionsOptions} options - The party, its matches and how to refresh.
 * @return {object} The view's state and actions; `ready` is false until the detail loads.
 */
export function usePartyActions({
  document,
  selfHref,
  detail,
  validMatches,
  refetchDetail,
  onMutated,
}: PartyActionsOptions) {
  const submits = usePartySubmits();
  const undoStacks = useReconcileUndoStacks();
  const notify = usePartyNotify();
  const { closePartyPanels, panels } = usePartyPanels();
  const modals = usePartyModals();
  const dialogOpen = useLatest(modals.confirmOpen || modals.deleteOpen);
  const banners = usePartyBanners();
  const [pickedSuggestionId, setPickedSuggestionId] = useState<number | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [auditRefreshToken, setAuditRefreshToken] = useState(0);
  const reconcileSession = useMemo(() => reconcileSessionId(), []);

  /** Reloads the party's activity feed. */
  const refreshActivity = () => setAuditRefreshToken((n) => n + 1);
  /** Refreshes the parties list, the party and its activity after a change. */
  const afterMutation = () => {
    onMutated();
    refetchDetail();
    refreshActivity();
  };
  const { forThisParty, refreshViewed } = useForThisParty({
    selfHref,
    refetchDetail,
    onMutated,
  });
  const run = usePartyRun({
    selfHref,
    detail,
    forThisParty,
    onFinished: afterMutation,
    setError,
  });

  // Reset per-party state during render, keyed on the party prop's href. The
  // modals block a user-driven switch, but the selection is index-based, so a
  // concurrent change to the parties list can still move it.
  const [viewedHref, setViewedHref] = useState(selfHref);
  if (viewedHref !== selfHref) {
    setViewedHref(selfHref);
    banners.clearTransientBanners();
    closePartyPanels();
    modals.closeMarkCorrect();
    modals.closeConfirms();
    setPickedSuggestionId(null);
    run.resetForParty();
    setError(null);
  }

  // The examiner's pick holds while it is still a match; the confirmed match,
  // else the top one, is selected otherwise.
  const confirmedId = detail?.suggestions.find(
    (s) => s.status === 'confirmed'
  )?.id;
  const pickIsMatch = validMatches.some((s) => s.id === pickedSuggestionId);
  const selectedSuggestionId =
    confirmedId ??
    (pickIsMatch ? pickedSuggestionId : (validMatches[0]?.id ?? null));

  // An action by Auto-match or another examiner resolved this party: anything
  // the examiner had open or picked acted on the old resolution. True when
  // something was discarded.
  /**
   * Closes everything the examiner had open or picked on the party.
   *
   * @return {boolean} True when something was discarded.
   */
  const discardOpenWork = () => {
    const discarded =
      panels.reconcileOpen ||
      modals.confirmOpen ||
      modals.deleteOpen ||
      modals.rerunOpen ||
      modals.revertOpen ||
      !!modals.flaggedForOverride ||
      panels.changeMatchOpen ||
      panels.canonizeOpen ||
      panels.stagedEditorOpen ||
      pickIsMatch;
    closePartyPanels();
    modals.closeMarkCorrect();
    modals.closeConfirms();
    setPickedSuggestionId(null);
    return discarded;
  };

  const shared = {
    notify,
    afterMutation,
    refreshActivity,
    forThisParty,
    discardOpenWork,
    ownSubmitInFlight: submits.ownSubmitInFlight,
    rerunPending: run.rerunPending,
    auditRefreshToken,
  };
  if (!detail) return { ready: false as const, ...shared };

  const undo = partyUndoStack(detail.id, undoStacks);
  const outcomes = partyOutcomes(detail, {
    forThisParty,
    refreshViewed,
    notify,
    setError,
    // A confirm cancelled while its submit was pending reports on the party.
    setDialogError: (message) =>
      (dialogOpen.current ? modals.setDialogError : setError)(message),
  });
  const ctx: PartyHandlerContext = {
    document,
    detail,
    triggers: submits.triggers,
    modals,
    banners,
    closePartyPanels,
    notify,
    afterMutation,
    refreshActivity,
    onMutated,
    forThisParty,
    outcomes,
    setError,
    startRun: run.startRun,
    discardOpenWork,
  };
  const handleConfirm = confirmHandler(ctx, selectedSuggestionId);
  const standing = standingHandlers(ctx);

  return {
    ready: true as const,
    ...shared,
    pending: submits.pending,
    confirmLoading: submits.confirmLoading,
    error,
    clearError: () => setError(null),
    // Reloading the party re-arms the poll while its run is still going; the
    // run may have finished meanwhile, so it refreshes what a finished poll does.
    retryProgress: run.progressLost ? afterMutation : null,
    selectedSuggestionId,
    pickSuggestion: setPickedSuggestionId,
    reconcileSession,
    reconcileUndoAvailable: undo.stack.length > 0,
    banners: banners.banners,
    markCorrect: {
      open: modals.confirmOpen,
      show: () => modals.setConfirmOpen(true),
      close: modals.closeMarkCorrect,
      preserveStaged: modals.preserveStaged,
      setPreserveStaged: modals.setPreserveStaged,
      aliasReason: modals.aliasReason,
      setAliasReason: modals.setAliasReason,
      submit: () => handleConfirm(),
    },
    dialogError: modals.dialogError,
    flagOverride: {
      flagged: modals.flaggedForOverride,
      cancel: () => {
        modals.setFlaggedForOverride(null);
        modals.setDialogError(null);
      },
      confirm: handleConfirm,
    },
    removeModal: {
      open: modals.deleteOpen,
      show: () => modals.setDeleteOpen(true),
      close: () => {
        modals.setDeleteOpen(false);
        modals.setDialogError(null);
      },
      submit: standing.handleRemove,
    },
    revertModal: {
      open: modals.revertOpen,
      close: () => modals.setRevertOpen(false),
      submit: standing.handleRevert,
    },
    // A revert is confirmed first unless the server reported it reopens no
    // other party.
    requestRevert: () => {
      if (
        detail.revertSharedWith &&
        !sharedAnchorMessage(detail.revertSharedWith)
      )
        standing.handleRevert();
      else modals.setRevertOpen(true);
    },
    rerunModal: {
      open: modals.rerunOpen,
      show: () => modals.setRerunOpen(true),
      close: () => modals.setRerunOpen(false),
      submit: standing.handleRerun,
    },
    panels,
    outcomes,
    ...reconcileHandlers(ctx, undo, reconcileSession),
    handleRevert: standing.handleRevert,
    handleRestore: standing.handleRestore,
  };
}
